import {
  usersAdminControllerList,
  usersControllerMe,
  type User,
  unwrapApiResponse,
} from "@cryptors/api-client";
import { useQuery } from "@tanstack/react-query";

import { getCurrentUserKey } from "@/providers/authProvider";

const PAGE_SIZE = 500;

type Page = { data: User[]; total: number };

const getCurrentUser = (): Promise<User> =>
  unwrapApiResponse(usersControllerMe<true>({ throwOnError: true }));

export const useGetCurrentUser = (enabled = true) =>
  useQuery({
    queryKey: ["users", "me", getCurrentUserKey()],
    queryFn: getCurrentUser,
    enabled,
  });

export const fetchAllUsers = async (): Promise<User[]> => {
  const fetchPage = async (page: number): Promise<Page> => {
    const data = await unwrapApiResponse(
      usersAdminControllerList<true>({
        query: { limit: PAGE_SIZE, page },
        throwOnError: true,
      }),
    );
    if (Array.isArray(data)) {
      return { data, total: data.length };
    }
    return data as Page;
  };

  const first = await fetchPage(1);
  const pageCount = Math.max(1, Math.ceil(first.total / PAGE_SIZE));
  if (pageCount <= 1) return first.data;

  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, i) => fetchPage(i + 2)),
  );
  return [first.data, ...rest.map((p) => p.data)].flat();
};
